import { useMemo } from "react"
import type { TableModel } from "utils/tableModel"
import type {
  BarTile,
  PieTile,
} from "features/Dashboard/types/dashboardDocument"
import { useDashboardStore } from "features/Dashboard/runtime/dashboardStore"
import { resolveLeafToCondition } from "features/Dashboard/runtime/clickResolver"

/**
 * Cross-filtering for a chart whose categories run along `axis` of the
 * model. `emits`: a click in View mode toggles the clicked category's
 * condition. `selected`: in View mode, the ids of the categories this tile
 * filters on (compared by condition, as the table tile does), or null when
 * it filters on nothing; the chart dims every other category.
 */
export const useCategoryCrossFilter = (
  tile: BarTile | PieTile,
  model: TableModel,
  axis: "down" | "across"
): {
  emits: boolean
  selected: Set<string> | null
  toggle: (categoryId: string) => void
} => {
  const mode = useDashboardStore((s) => s.mode)
  const crossFilter = useDashboardStore((s) => s.crossFilters[tile.id])
  const toggleCrossFilter = useDashboardStore((s) => s.toggleCrossFilter)
  const { source } = tile
  const view = mode === "view"
  const emits = view && tile.crossFilter.emits && source.kind === "builder"

  const selected = useMemo(() => {
    if (!view || !crossFilter || source.kind !== "builder") return null
    const ids = new Set<string>()
    for (const leaf of model[axis].leaves) {
      if (leaf.isTotal || ids.has(leaf.id)) continue
      const cond = resolveLeafToCondition({ leaf, axis, source })
      if (
        cond &&
        cond.variable === crossFilter.variable &&
        crossFilter.categories.includes(cond.category)
      ) {
        ids.add(leaf.id)
      }
    }
    return ids
  }, [view, crossFilter, model, axis, source])

  const toggle = (categoryId: string) => {
    if (!emits || source.kind !== "builder") return
    // A measure is not a category: a net is no codebook path.
    if (tile.measures.some((m) => m.id === categoryId)) return
    const leaf = model[axis].leaves.find(
      (l) => l.id === categoryId && !l.isTotal
    )
    if (!leaf) return
    const cond = resolveLeafToCondition({ leaf, axis, source })
    if (cond) toggleCrossFilter(tile.id, cond.variable, cond.category)
  }

  return { emits, selected, toggle }
}
